import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, User, ArrowLeft } from 'lucide-react';

// Blog data
const getBlogData = (slug) => {
  const blogs = {
    'web-design-trends-2026': {
      title: 'Why Every Business Needs a Scalable Website in 2026',
      image: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=1200',
      author: 'Tecfinity Team',
      date: 'January 15, 2026',
      readTime: '8 min read',
      introduction: "In today's digital-first world, your website is more than an online presence—it's the foundation of your brand's identity and growth. As we enter 2026, scalability has become the defining factor for businesses that want to thrive. A scalable website doesn't just look good; it adapts, grows, and performs seamlessly as your business expands. Whether you're a startup with a small audience or an enterprise managing global traffic, scalability ensures your website can handle it all without compromising speed, security, or user experience.",
      sections: [
        {
          heading: 'The Evolution of Business Websites',
          content: "Years ago, websites were static brochures—simple pages with company information. Today, they are powerful business platforms, serving as storefronts, marketing tools, and customer service hubs. Businesses are no longer asking, 'Do we need a website?' but instead, 'Can our website keep up with growth?' Scalability answers this question by future-proofing your online presence."
        },
        {
          heading: 'What Does Scalability Mean for Websites?',
          content: 'Scalability refers to your website\'s ability to handle increased demand without breaking. This includes:',
          list: [
            'Traffic Growth – Can your site support 10,000 visitors today and 100,000 tomorrow?',
            'Feature Expansion – Adding e-commerce, chatbots, or integrations without slowing performance.',
            'Security Demands – Managing data safely as user numbers grow.'
          ],
          afterList: 'In short, a scalable website evolves with your business, preventing downtime and user frustration.'
        },
        {
          heading: 'Why Scalability Matters in 2026',
          subSections: [
            {
              subheading: '1. Rising Online Competition',
              text: 'With more businesses moving online, customers expect fast, reliable websites. A slow or crashing site can cost you conversions instantly.'
            },
            {
              subheading: '2. Mobile-First World',
              text: 'Mobile usage continues to dominate. Scalable sites ensure smooth performance across devices, from smartphones to tablets.'
            },
            {
              subheading: '3. Evolving Technology',
              text: 'AI chatbots, voice search, and AR integration are no longer futuristic—they\'re here. Only scalable websites can seamlessly adopt these advancements.'
            },
            {
              subheading: '4. Customer Expectations',
              text: 'Today\'s users won\'t wait more than 3 seconds for a page to load. Scalability ensures you meet these expectations consistently.'
            }
          ]
        },
        {
          heading: 'Key Benefits of a Scalable Website',
          list: [
            'Future-Proof Growth - No need for a redesign every time your business expands.',
            'Cost-Effective – Investing in scalability upfront prevents costly rebuilds later.',
            'Improved Security – Protects sensitive customer data as volumes increase.',
            'Enhanced User Experience – Fast, reliable websites build customer trust and loyalty.'
          ]
        },
        {
          heading: 'Real-World Example',
          content: 'Imagine a small e-commerce brand launching new products during holiday sales. Without scalability, their site crashes under heavy traffic, resulting in lost revenue and disappointed customers. In contrast, a scalable website handles spikes seamlessly, boosting sales and reputation.'
        },
        {
          heading: 'How Invotage Helps',
          content: 'We design scalable websites tailored to your business. Our web development team ensures your site is fast, secure, and ready to grow with you. From startups to enterprises, we build solutions that adapt to future needs.'
        }
      ],
      conclusion: "In 2026, having a website is not enough—it must be scalable. As businesses face rising competition, customer expectations, and evolving technology, scalability is the key to staying ahead. Don't let growth become a barrier. Instead, invest in a scalable website that supports your vision and drives long-term success."
    },
    'mobile-first-development': {
      title: 'Mobile-First Development: Why It Matters Now More Than Ever',
      image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=1200',
      author: 'Tecfinity Team',
      date: 'January 12, 2026',
      readTime: '7 min read',
      introduction: "Mobile devices have fundamentally transformed how users interact with the web. With over 73% of global web traffic coming from mobile devices, the question is no longer whether to optimize for mobile—it's how well you do it. Mobile-first development isn't just a trend; it's a necessity for businesses that want to remain competitive in 2026 and beyond.",
      sections: [
        {
          heading: 'What is Mobile-First Development?',
          content: 'Mobile-first development is a design and development approach that prioritizes the mobile user experience before scaling up to larger screens. Instead of building a desktop website and then squeezing it into a mobile view, mobile-first design starts with the smallest screen and progressively enhances the experience for tablets and desktops.',
          afterList: 'This approach ensures that the most critical content and functionality work flawlessly on mobile devices, where the majority of your users are.'
        },
        {
          heading: 'Why Mobile-First Matters in 2026',
          subSections: [
            {
              subheading: '1. Mobile Traffic Dominates',
              text: 'Statistics show that mobile devices account for 73% of all web traffic. If your site isn\'t optimized for mobile, you\'re potentially losing three-quarters of your audience.'
            },
            {
              subheading: '2. Google\'s Mobile-First Indexing',
              text: 'Google now uses the mobile version of your website for indexing and ranking. If your mobile site is slow or poorly designed, your search rankings suffer—regardless of how good your desktop version is.'
            },
            {
              subheading: '3. User Expectations Have Changed',
              text: 'Modern users expect instant access to information on their phones. Sites that load slowly or have clunky navigation on mobile lose visitors within seconds.'
            },
            {
              subheading: '4. Higher Conversion Rates',
              text: 'Mobile-optimized sites see significantly higher conversion rates. Users are more likely to complete purchases, sign up for services, or fill out forms when the experience is seamless.'
            }
          ]
        },
        {
          heading: 'Key Principles of Mobile-First Design',
          list: [
            'Simplified Navigation – Touch-friendly menus and buttons designed for thumbs, not cursors.',
            'Fast Loading Times – Compress images, minimize code, and use lazy loading to ensure pages load in under 3 seconds.',
            'Responsive Layouts – Flexible grids and breakpoints that adapt smoothly across all screen sizes.',
            'Readable Typography – Larger fonts and proper spacing to ensure text is easy to read on small screens.',
            'Touch-Optimized Interactions – Buttons and interactive elements sized appropriately for finger taps.'
          ]
        },
        {
          heading: 'The Business Impact',
          content: 'Companies that adopt mobile-first strategies see measurable results. Studies show that mobile-optimized websites experience 160% higher conversion rates compared to non-optimized sites. Additionally, page load times directly impact revenue—Amazon found that every 100ms delay costs them 1% in sales.'
        },
        {
          heading: 'Real-World Success Story',
          content: 'A retail client came to Invotage with a desktop-heavy website that performed poorly on mobile. After implementing a mobile-first redesign, their mobile conversions increased by 240%, bounce rates dropped by 35%, and average session duration doubled. The result? A 3x increase in overall revenue within six months.'
        },
        {
          heading: 'How Invotage Implements Mobile-First',
          content: 'At Invotage, we don\'t just build mobile-friendly sites—we build mobile-first experiences. Our process includes user testing on real devices, performance optimization, and progressive enhancement strategies to ensure your site works beautifully everywhere. From wireframes to deployment, we prioritize the mobile experience at every stage.'
        }
      ],
      conclusion: "Mobile-first development is no longer optional—it's essential for survival in today's digital landscape. With the majority of users browsing on mobile devices, businesses that fail to prioritize mobile experiences will fall behind. Invest in mobile-first design, and you'll not only meet user expectations but exceed them, driving engagement, conversions, and long-term growth."
    },
    'ai-integration-business-software': {
      title: 'AI Integration in Modern Business Software: The Future is Now',
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1200',
      author: 'Tecfinity Team',
      date: 'January 8, 2026',
      readTime: '9 min read',
      introduction: "Artificial intelligence is no longer science fiction—it's a practical business tool transforming how companies operate. From automating repetitive tasks to predicting customer behavior, AI integration in business software has become essential for companies looking to stay competitive. In 2026, businesses that leverage AI gain significant advantages in efficiency, decision-making, and customer satisfaction.",
      sections: [
        {
          heading: 'What is AI Integration?',
          content: 'AI integration refers to embedding artificial intelligence technologies—such as machine learning, natural language processing, and predictive analytics—into existing business software and workflows. This can include chatbots, recommendation engines, automated data analysis, and intelligent process automation.',
          afterList: 'The goal isn\'t to replace humans but to augment their capabilities, freeing teams to focus on strategic work while AI handles routine tasks.'
        },
        {
          heading: 'Key Areas Where AI Makes an Impact',
          subSections: [
            {
              subheading: '1. Customer Service Automation',
              text: 'AI-powered chatbots and virtual assistants handle customer inquiries 24/7, providing instant responses and resolving common issues without human intervention. This reduces wait times and improves customer satisfaction.'
            },
            {
              subheading: '2. Predictive Analytics',
              text: 'Machine learning algorithms analyze historical data to predict future trends, helping businesses make informed decisions about inventory, marketing campaigns, and resource allocation.'
            },
            {
              subheading: '3. Personalization',
              text: 'AI enables hyper-personalized user experiences by analyzing behavior patterns and preferences. E-commerce platforms use this to recommend products, while content platforms suggest relevant articles and videos.'
            },
            {
              subheading: '4. Process Automation',
              text: 'Robotic Process Automation (RPA) combined with AI can handle complex workflows—from invoice processing to employee onboarding—reducing errors and saving countless hours.'
            },
            {
              subheading: '5. Data Security',
              text: 'AI-powered security systems detect anomalies and potential threats in real-time, protecting sensitive business data from cyber attacks more effectively than traditional methods.'
            }
          ]
        },
        {
          heading: 'Benefits of AI Integration',
          list: [
            'Increased Efficiency – Automate repetitive tasks and free up employee time for high-value work.',
            'Cost Savings – Reduce operational costs by streamlining processes and minimizing errors.',
            'Better Decision-Making – Leverage data-driven insights to make smarter business decisions.',
            'Enhanced Customer Experience – Provide instant, personalized service that delights customers.',
            'Competitive Advantage – Stay ahead of competitors who haven\'t adopted AI technologies.',
            'Scalability – AI systems can handle growing workloads without proportional increases in costs.'
          ]
        },
        {
          heading: 'Real-World Implementation Example',
          content: 'A logistics company partnered with Invotage to integrate AI into their dispatch system. The AI analyzed traffic patterns, weather conditions, and delivery histories to optimize routes in real-time. The results were remarkable: delivery times decreased by 30%, fuel costs dropped by 22%, and customer satisfaction scores increased by 45%.'
        },
        {
          heading: 'Common Misconceptions About AI',
          subSections: [
            {
              subheading: 'Myth 1: AI is Too Expensive',
              text: 'Reality: Cloud-based AI services have made implementation affordable for businesses of all sizes. The ROI often justifies the investment within months.'
            },
            {
              subheading: 'Myth 2: AI Will Replace Jobs',
              text: 'Reality: AI augments human work rather than replacing it. Employees transition to more strategic, creative roles while AI handles routine tasks.'
            },
            {
              subheading: 'Myth 3: AI is Too Complex',
              text: 'Reality: Modern AI tools are designed for business users, not just data scientists. With the right partner, implementation is straightforward.'
            }
          ]
        },
        {
          heading: 'How Invotage Approaches AI Integration',
          content: 'At Invotage, we specialize in practical AI implementations that deliver measurable results. We start by identifying high-impact use cases in your business, then design custom AI solutions that integrate seamlessly with your existing systems. Our team handles everything from data preparation to model training and deployment, ensuring you get maximum value from AI technology.'
        }
      ],
      conclusion: "AI integration is transforming business software from static tools into intelligent systems that learn, adapt, and improve over time. Companies that embrace AI today position themselves for success in an increasingly competitive landscape. Whether you're looking to automate customer service, optimize operations, or unlock insights from your data, AI offers solutions that drive real business results. The future isn't coming—it's already here."
    }
  };

  return blogs[slug];
};

const BlogPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const blog = getBlogData(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!blog) {
    return (
      <div className="min-h-screen pt-24 pb-12 bg-gradient-to-br from-gray-900 via-black to-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Blog Not Found</h1>
          <Link to="/blogs" className="text-purple-400 hover:text-purple-300">
            ← Back to Blogs
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Back Button */}
      <div className="pt-24 pb-4 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate('/blogs')}
          className="flex items-center gap-2 text-gray-400 hover:text-purple-400 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Blogs</span>
        </button>
      </div>

      {/* Hero Section */}
      <div className="relative h-[400px] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-gray-900/50 to-gray-900" />
        <img
          src={blog.image}
          alt={blog.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 w-full">
            <div className="inline-block px-4 py-1 bg-purple-600/20 backdrop-blur-sm border border-purple-500/30 rounded-full text-sm text-purple-300 mb-4">
              OUR BLOGS
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              {blog.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-gray-300">
              <div className="flex items-center gap-2">
                <User className="w-5 h-5" />
                <span>{blog.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                <span>{blog.date}</span>
              </div>
              <div className="px-3 py-1 bg-purple-600/20 rounded-full text-sm">
                {blog.readTime}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Blog Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Introduction */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-white mb-6">Introduction</h2>
          <p className="text-lg text-gray-300 leading-relaxed">
            {blog.introduction}
          </p>
        </div>

        {/* Sections */}
        {blog.sections.map((section, index) => (
          <div key={index} className="mb-12">
            <h2 className="text-3xl font-bold text-white mb-6">
              {section.heading}
            </h2>
            
            {section.content && (
              <p className="text-lg text-gray-300 leading-relaxed mb-4">
                {section.content}
              </p>
            )}

            {section.list && (
              <ul className="space-y-3 mb-4">
                {section.list.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-purple-500 mt-2 flex-shrink-0" />
                    <span className="text-lg text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
            )}

            {section.afterList && (
              <p className="text-lg text-gray-300 leading-relaxed mt-4">
                {section.afterList}
              </p>
            )}

            {section.subSections && (
              <div className="space-y-6 mt-6">
                {section.subSections.map((sub, subIdx) => (
                  <div key={subIdx} className="pl-6 border-l-2 border-purple-500/30">
                    <h3 className="text-xl font-semibold text-white mb-3">
                      {sub.subheading}
                    </h3>
                    <p className="text-lg text-gray-300 leading-relaxed">
                      {sub.text}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* Conclusion */}
        <div className="mt-16 p-8 bg-gradient-to-br from-purple-900/20 to-blue-900/20 rounded-3xl border border-purple-500/20">
          <h2 className="text-3xl font-bold text-white mb-6">Conclusion</h2>
          <p className="text-lg text-gray-300 leading-relaxed">
            {blog.conclusion}
          </p>
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center p-12 bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-3xl border border-purple-500/20">
          <h3 className="text-3xl font-bold text-white mb-4">
            Ready to Transform Your Business?
          </h3>
          <p className="text-lg text-gray-300 mb-8">
            Let's discuss how Invotage can help you achieve your goals.
          </p>
          <Link
            to="/contact"
            className="inline-block px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold rounded-full transition-all shadow-lg shadow-purple-500/50"
          >
            Get Started Today
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BlogPage;
